/**
 * GROUP BY scope model — what a bare column reference may name once the
 * statement groups.
 *
 * ---------------------------------------------------------------------------
 * THE MODEL
 * ---------------------------------------------------------------------------
 * When the statement the caret sits in has a GROUP BY clause, the row shape
 * downstream of that clause is no longer the FROM relation's row — it is one
 * row per group. From that point on the only legal BARE column references are:
 *
 *   (a) the GROUP BY expressions themselves — plain columns, or (where the
 *       engine allows it) a select-list alias / an ordinal standing for one;
 *   (b) any column, grouped or not, INSIDE an aggregate call's parens
 *       (`HAVING SUM(▮` is the full column vocabulary again);
 *   (c) select-list aliases — ORDER BY only in the strict engines (MSSQL /
 *       Oracle / PostgreSQL reject them in HAVING; MySQL accepts them).
 *
 * Everything else — a raw, non-grouped, non-aggregated column — is an error on
 * PostgreSQL / MSSQL / Oracle, and on MySQL (with ONLY_FULL_GROUP_BY off) or
 * SQLite it is accepted but returns an arbitrary row's value, which is almost
 * always a bug.
 *
 * So the two actions this model can ask for are:
 *   - `'filter'` — drop non-grouped raw columns from the candidate list. Used
 *     at HAVING / ORDER BY on the engines where they are a hard error; the
 *     coverage rows this closes are must-NOTs, so re-ranking is not enough.
 *   - `'demote'` — keep them, ranked below the grouped ones. Used on the lax
 *     engines, and at the select list on every engine (see SCOPE below).
 *
 * ---------------------------------------------------------------------------
 * THE ENGINE MATRIX (one cell per claim, each with its source)
 * ---------------------------------------------------------------------------
 * See `GROUP_BY_RULES` below. Summary:
 *
 * | engine | alias in GROUP BY | ordinal in GROUP BY | alias in HAVING | alias in ORDER BY | bare non-grouped column |
 * |--------|-------------------|---------------------|-----------------|-------------------|-------------------------|
 * | pgsql  | yes               | yes                 | no              | yes               | error   → filter        |
 * | mysql  | yes               | yes                 | yes             | yes               | mode-dependent → demote |
 * | mssql  | no                | no                  | no              | yes               | error   → filter        |
 * | oracle | no                | no                  | no              | yes               | error   → filter        |
 * | sqlite | yes               | yes                 | yes             | yes               | accepted → demote       |
 *
 * ---------------------------------------------------------------------------
 * CONFIDENCE GATE — wrong filtering silently hides legal columns
 * ---------------------------------------------------------------------------
 * The filter runs ONLY when the GROUP BY clause parses with confidence:
 * plain (optionally qualified) column refs, select-list aliases, ordinals, or
 * a simple operator expression over plain column refs. Anything else bails and
 * the candidate list is returned untouched:
 *
 *   - `GROUPING SETS` / `ROLLUP` / `CUBE` / `GROUP BY ALL` / `GROUP BY ()` —
 *     the grouped set is not a flat column list;
 *   - a GROUP BY item containing a function call (`GROUP BY date_trunc('day',
 *     created_at)`) — the legal reference is the whole expression, which we
 *     cannot match against a flat column candidate;
 *   - a keyword inside a GROUP BY item (`COLLATE`, a column whose name is a
 *     reserved word) — an unrecognised item shape;
 *   - a set-operation compound (`… UNION …`) — the trailing ORDER BY belongs
 *     to the compound, not to either branch's GROUP BY;
 *   - a caret at paren depth > 0 — this is (b), the aggregate-argument escape:
 *     `HAVING SUM(▮`, `WITHIN GROUP (ORDER BY ▮`, `OVER (ORDER BY ▮` all keep
 *     the full column set;
 *   - a dotted caret (`ORDER BY p.▮`) — v1 scope: qualified access keeps its
 *     own emission path. Note the GROUP BY *side* of qualification IS handled:
 *     `GROUP BY p.customer_id` makes both `customer_id` and `p.customer_id`
 *     legal, because items are matched on their last path segment;
 *   - no GROUP BY clause in the statement at all, or more than one at depth 0;
 *   - and — the last-resort guard — when the parse produced grouped names but
 *     NONE of them matches an emitted column candidate, which means the parse
 *     and the emission are talking about different relations.
 *
 * ---------------------------------------------------------------------------
 * SCOPE (deliberate v1 boundaries)
 * ---------------------------------------------------------------------------
 * - `positionKind === 'scalar-value'` is EXEMPT. A typed comparison operand
 *   (`HAVING SUM(x) > ▮`) is served by the value-slot policy, whose candidate
 *   set is driven by the LHS type rather than by the clause, and the spec rows
 *   at those carets (pgsql script.41 §7, pgsql script.16 §3, mssql script.28
 *   §2, oracle script.27 §3, sqlite script.13 §3) pin raw numeric columns as
 *   the legal operand. Narrowing those is a separate change to those rows.
 * - The select list DEMOTES rather than filters even on the strict engines.
 *   `SELECT a, ▮ FROM t GROUP BY a` is the middle of the commonest editing
 *   flow there is — add the projection, then add it to GROUP BY — and hiding
 *   the column the user is about to group by would break that flow. HAVING and
 *   ORDER BY have no such flow: the grouping is already written.
 *
 * ---------------------------------------------------------------------------
 * WIRING
 * ---------------------------------------------------------------------------
 * `applyGroupByScopeFilter` is called once per resolver (pgsql / mysql /
 * mssql / oracle / sqlite), in the post-build filter chain next to
 * `applyParenScopeFilter` / `applyIndexTargetFilter`. That is the single point
 * where every arm's candidates converge, so one call covers the clause arm
 * (`positionKind` `'having'` / `'order-by'` / `'select-list'`), the
 * predicate-head arm (`HAVING ▮` classifies as a head, and the clause
 * underneath it comes from `computeClauseContext`) and the value-slot arms'
 * conservative refusals — without five copies of the gate drifting apart.
 *
 * Reuses the existing machinery rather than adding a SQL walker: the clause
 * machine (`sql-grammar/document/editorContextClause.computeClauseContext`),
 * the shared statement tokenizer (`context/tokenizer.tokenize`), the region
 * scan (`context/slotTextScan.scanRegion`) for paren depth, and
 * `resolver/selectListAliases.extractSelectListAliases` for the alias set.
 * Its sibling at the GROUP BY caret itself is
 * `fkJoin/projectionAwareRanking.applyProjectionAwareGroupByRanking`, which
 * lifts already-projected columns while the user is WRITING the grouping;
 * this model governs the clauses that come after it.
 */

import type {
  CompletionCandidate,
  CompletionCandidateTier,
  CompletionPositionKind,
  DatabaseEngineId,
} from '@/core/engines';
import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';

import { extractSelectListAliases } from '../resolver/selectListAliases';
import { parseIdentifierToken } from '../resolver/identifierUtils';
import { databaseEngineIdToSqlEngine } from '../versionGating/engineIdMapping';

import { scanOptionsFor, scanRegion } from './slotTextScan';
import { tokenize, type Token } from './tokenizer';

// ---------------------------------------------------------------------------
// Engine matrix
// ---------------------------------------------------------------------------

/** What a bare, non-grouped, non-aggregated column reference does downstream
 *  of a GROUP BY on this engine. */
type BareNonGroupedColumn =
  /** Hard error — the engine refuses to run the statement. */
  | 'error'
  /** Accepted, value is an arbitrary row from the group — almost always a bug. */
  | 'accepted';

type GroupByEngineRules = {
  /** `GROUP BY <select-list alias>` resolves to the aliased expression. */
  readonly aliasInGroupBy: boolean;
  /** `GROUP BY 1, 2` resolves to select-list positions. */
  readonly ordinalInGroupBy: boolean;
  /** `HAVING <select-list alias>` is a legal reference. */
  readonly aliasInHaving: boolean;
  /** `ORDER BY <select-list alias>` is a legal reference. */
  readonly aliasInOrderBy: boolean;
  readonly bareNonGroupedColumn: BareNonGroupedColumn;
};

export const GROUP_BY_RULES: Readonly<Record<string, GroupByEngineRules>> = {
  pgsql: {
    // PG docs, SELECT / GROUP BY: "In strict SQL, GROUP BY can only group by
    // columns of the source table but PostgreSQL extends this to also allow
    // GROUP BY to group by columns in the select list."
    aliasInGroupBy: true,
    // Same section: "an output column name or ordinal number".
    ordinalInGroupBy: true,
    // PG docs, SELECT / HAVING: output-column names are NOT substituted in
    // HAVING — the aggregate has to be repeated.
    aliasInHaving: false,
    // PG docs, SELECT / ORDER BY: "an output column name or number".
    aliasInOrderBy: true,
    // PG: ERROR 42803 "column … must appear in the GROUP BY clause or be used
    // in an aggregate function".
    bareNonGroupedColumn: 'error',
  },
  mysql: {
    // MySQL manual, "MySQL Handling of GROUP BY": aliases are permitted in
    // GROUP BY (a documented extension to standard SQL).
    aliasInGroupBy: true,
    // MySQL 8.0 removed positional GROUP BY from ORDER BY-style deprecation
    // but still accepts `GROUP BY <n>`; kept `true` — it only ever ADDS a
    // grouped name, so a wrong `true` cannot hide a column.
    ordinalInGroupBy: true,
    // MySQL manual, "Problems with Column Aliases": an alias IS allowed in
    // HAVING (another documented extension).
    aliasInHaving: true,
    aliasInOrderBy: true,
    // Legal only because ONLY_FULL_GROUP_BY may be off (it is ON by default
    // since 5.7.5). We cannot see the session's sql_mode from here, so the
    // reference stays offered and is merely ranked down.
    bareNonGroupedColumn: 'accepted',
  },
  mssql: {
    // T-SQL, SELECT - GROUP BY: the clause takes expressions over the FROM
    // rows; a select-list alias is not in scope there.
    aliasInGroupBy: false,
    // T-SQL has no positional GROUP BY.
    ordinalInGroupBy: false,
    // T-SQL, SELECT - HAVING: same scope as GROUP BY — no alias substitution.
    aliasInHaving: false,
    // T-SQL, SELECT - ORDER BY: "column_alias … can be used".
    aliasInOrderBy: true,
    // MSSQL: Msg 8120 "Column … is invalid in the select list because it is
    // not contained in either an aggregate function or the GROUP BY clause."
    bareNonGroupedColumn: 'error',
  },
  oracle: {
    // Oracle SQL Language Reference, SELECT / group_by_clause: expressions
    // over the selected rows; column aliases are not in scope.
    aliasInGroupBy: false,
    ordinalInGroupBy: false,
    // Oracle rejects a column alias in HAVING (the alias is not in scope
    // until the ORDER BY step). NOTE: this cell is reported, not enforced —
    // see ALIAS CANDIDATES below.
    aliasInHaving: false,
    // Oracle SQL Language Reference, SELECT / order_by_clause: "c_alias".
    aliasInOrderBy: true,
    // Oracle: ORA-00979 "not a GROUP BY expression".
    bareNonGroupedColumn: 'error',
  },
  sqlite: {
    // SQLite, "SELECT": GROUP BY takes result-column names as an extension.
    aliasInGroupBy: true,
    ordinalInGroupBy: true,
    aliasInHaving: true,
    aliasInOrderBy: true,
    // SQLite accepts a bare non-aggregated column in an aggregate query and
    // returns a value from an arbitrary row of the group — documented
    // behaviour, so it is demoted rather than removed.
    bareNonGroupedColumn: 'accepted',
  },
};

// ALIAS CANDIDATES — the `aliasInHaving` cell is reported on the scope object
// but does NOT strip `kind: 'alias'` candidates at HAVING. Two reasons: the
// per-engine spec rows document the select-list alias as offered at HAVING on
// every engine (oracle script.57 §1 pins `avg_sal`, mssql script.51 §1's
// head-of-list carries `cnt`), and a user who is EDITING an existing
// `HAVING <alias> …` still wants the name completable even where the engine
// would reject it. This model's mandate is the non-grouped raw column, which
// is the actual must-NOT in the register.

// ---------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------

/** Which post-GROUP-BY clause the caret is in. */
export type GroupByScopeClause = 'having' | 'order-by' | 'select-list';

export type GroupByScope = {
  readonly clause: GroupByScopeClause;
  readonly action: 'filter' | 'demote';
  /** Folded names a bare column reference may legally take. */
  readonly groupedNames: ReadonlySet<string>;
  /** Folded select-list alias names in scope for this statement. */
  readonly aliasNames: ReadonlySet<string>;
  /** Matrix cell: is a select-list alias a legal reference in THIS clause? */
  readonly aliasReferenceLegal: boolean;
};

/** Pre-flight: does the statement even mention `GROUP`? (Not `/g` — a shared
 *  stateful regex would skip matches between calls.) */
const HAS_GROUP_WORD = /\bgroup\b/i;

export type GroupByScopeArgs = {
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  readonly statementEnd: number;
  readonly engineId: DatabaseEngineId;
  readonly positionKind: CompletionPositionKind;
};

/**
 * The GROUP BY scope in force at the caret, or `null` when the model declines
 * to say (no GROUP BY, an unparsed grouping, an aggregate-argument caret, a
 * clause the model does not govern — see the CONFIDENCE GATE above).
 */
export function resolveGroupByScope(args: GroupByScopeArgs): GroupByScope | null {
  const rules = GROUP_BY_RULES[args.engineId];
  if (!rules) return null;

  // Cheapest possible bail first — this runs on every keystroke at every
  // select-list and unnamed position, and the overwhelming majority of
  // statements have no GROUP BY at all. Skips the clause machine and the
  // statement tokenize below.
  if (!HAS_GROUP_WORD.test(args.text.slice(args.statementStart, args.statementEnd))) {
    return null;
  }

  const clause = scopeClauseAt(args);
  if (clause === null) return null;

  // (b) the aggregate-argument escape. Any unclosed paren between the
  // statement start and the caret means the caret is inside a call's or a
  // window's parens, where every column is legal again.
  const opts = scanOptionsFor(args.engineId);
  if (scanRegion(args.text, args.statementStart, args.offset, opts).parenDepth > 0) {
    return null;
  }

  const tokens = tokenize(args.text, args.statementStart, args.statementEnd, args.engineId);
  if (tokens.length === 0) return null;
  const depths = tokenDepths(tokens);

  // A set-operation compound's trailing ORDER BY is scoped to the compound's
  // output columns, not to either branch's GROUP BY.
  for (let i = 0; i < tokens.length; i += 1) {
    if (depths[i] !== 0 || tokens[i]!.type !== 'kw') continue;
    if (SET_OPERATION_KEYWORDS.has(tokens[i]!.text.toUpperCase())) return null;
  }

  const groupBy = findGroupByClause(tokens, depths);
  if (groupBy === null) return null;

  const aliases = new Set(
    extractSelectListAliases(
      args.text,
      { start: args.statementStart, end: args.statementEnd },
      args.offset,
      args.engineId,
    ).map((a) => a.name.toLowerCase()),
  );

  const groupedNames = collectGroupedNames(tokens, depths, groupBy, rules);
  if (groupedNames === null || groupedNames.size === 0) return null;

  const strict = rules.bareNonGroupedColumn === 'error';
  return {
    clause,
    // The select list demotes on every engine — see SCOPE above.
    action: strict && clause !== 'select-list' ? 'filter' : 'demote',
    groupedNames,
    aliasNames: aliases,
    aliasReferenceLegal: clause === 'having' ? rules.aliasInHaving : rules.aliasInOrderBy,
  };
}

/**
 * Apply the GROUP BY scope to a resolver's candidate list: drop (strict
 * engines, HAVING / ORDER BY) or demote (lax engines, and the select list)
 * the column candidates the grouping made illegal. Non-column candidates —
 * aggregate functions, keywords, aliases, relation names, variables — are
 * never touched. Returns the input array unchanged when no scope is in force.
 */
export function applyGroupByScopeFilter(
  candidates: CompletionCandidate[],
  args: GroupByScopeArgs,
): CompletionCandidate[] {
  const scope = resolveGroupByScope(args);
  if (scope === null) return candidates;

  // Last-resort guard (mirrors `applyProjectionAwareGroupByRanking`): if not
  // one emitted column matches a grouped name, the GROUP BY we parsed and the
  // columns we are about to filter belong to different relations, and acting
  // on that would hide the whole column list.
  let sawGrouped = false;
  for (const c of candidates) {
    if (c.kind === 'column' && scope.groupedNames.has(foldCandidateName(c))) {
      sawGrouped = true;
      break;
    }
  }
  if (!sawGrouped) return candidates;

  if (scope.action === 'filter') {
    return candidates.filter(
      (c) => c.kind !== 'column' || scope.groupedNames.has(foldCandidateName(c)),
    );
  }
  return candidates.map((c) => {
    if (c.kind !== 'column' || scope.groupedNames.has(foldCandidateName(c))) return c;
    return c.tier <= 2 ? { ...c, tier: DEMOTED_TIER } : c;
  });
}

/** Tier a legal-but-probably-a-bug non-grouped column drops to. Below the
 *  grouped columns and the select-list aliases (tier 1), above the relation
 *  flood. */
const DEMOTED_TIER: CompletionCandidateTier = 4;

// ---------------------------------------------------------------------------
// Clause gate
// ---------------------------------------------------------------------------

function scopeClauseAt(args: GroupByScopeArgs): GroupByScopeClause | null {
  switch (args.positionKind) {
    case 'having':
      return 'having';
    case 'order-by':
      return 'order-by';
    case 'select-list':
      return 'select-list';
    // `HAVING ▮` classifies as a predicate HEAD, and a clause the machine
    // could not name still has a clause underneath it — ask the machine.
    case 'predicate-head':
    case 'unknown': {
      const sqlEngine = databaseEngineIdToSqlEngine(args.engineId);
      if (!sqlEngine) return null;
      const clause = computeClauseContext(
        args.text,
        args.statementStart,
        args.offset,
        sqlEngine,
      )?.clause;
      if (clause === 'having') return 'having';
      if (clause === 'order-by') return 'order-by';
      return null;
    }
    // Everything else — `dotted-access`, `scalar-value`, `where`, `from`,
    // `group-by` itself, every DDL and procedural slot — is out of scope.
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// GROUP BY clause parse
// ---------------------------------------------------------------------------

const SET_OPERATION_KEYWORDS: ReadonlySet<string> = new Set([
  'UNION',
  'INTERSECT',
  'EXCEPT',
]);

/** Keywords that end the GROUP BY clause at depth 0. */
const GROUP_BY_TERMINATORS: ReadonlySet<string> = new Set([
  'HAVING',
  'ORDER',
  'LIMIT',
  'OFFSET',
  'FETCH',
  'WINDOW',
  'FOR',
  'INTO',
  'UNION',
  'INTERSECT',
  'EXCEPT',
  // NOTE: `WITH` is deliberately NOT a terminator — MySQL's `GROUP BY x WITH
  // ROLLUP` has to stay INSIDE the clause so the unparseable-grouping bail
  // below sees it.
]);

/** Words that make the grouped set something other than a flat column list. */
const UNPARSEABLE_GROUPING_WORDS: ReadonlySet<string> = new Set([
  'ROLLUP',
  'CUBE',
  'GROUPING',
  'SETS',
  // `GROUP BY ALL` (T-SQL's legacy form, and the DuckDB/Databricks "group by
  // every non-aggregated select item" form) names no explicit column list.
  'ALL',
  'DISTINCT',
  // MySQL's `GROUP BY x WITH ROLLUP`.
  'WITH',
]);

/** Keywords that terminate the SELECT list at depth 0. */
const SELECT_LIST_TERMINATORS: ReadonlySet<string> = new Set([
  'FROM',
  'WHERE',
  'GROUP',
  'HAVING',
  'ORDER',
  'LIMIT',
  'OFFSET',
  'FETCH',
  'INTO',
  'UNION',
  'INTERSECT',
  'EXCEPT',
]);

/** Paren depth of every token, so depth-0 scans need one pass. */
function tokenDepths(tokens: readonly Token[]): number[] {
  const depths: number[] = new Array(tokens.length);
  let depth = 0;
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'rparen') depth = Math.max(0, depth - 1);
    depths[i] = depth;
    if (t.type === 'lparen') depth += 1;
  }
  return depths;
}

type TokenSpan = { readonly from: number; readonly to: number };

/** The `GROUP BY` item list at depth 0 (token index range, `BY` excluded).
 *  `null` when there is no such clause, or more than one. */
function findGroupByClause(tokens: readonly Token[], depths: readonly number[]): TokenSpan | null {
  let start = -1;
  for (let i = 0; i < tokens.length - 1; i += 1) {
    if (depths[i] !== 0) continue;
    const t = tokens[i]!;
    if (t.type !== 'kw' || t.text.toUpperCase() !== 'GROUP') continue;
    const next = tokens[i + 1]!;
    if (next.type !== 'kw' || next.text.toUpperCase() !== 'BY') continue;
    // A second depth-0 GROUP BY means more than one grouped query is in the
    // statement range; we cannot tell which one owns the caret.
    if (start !== -1) return null;
    start = i + 2;
  }
  if (start === -1 || start >= tokens.length) return null;

  let end = tokens.length;
  for (let i = start; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'semicolon' && depths[i] === 0) {
      end = i;
      break;
    }
    if (depths[i] !== 0 || t.type !== 'kw') continue;
    if (GROUP_BY_TERMINATORS.has(t.text.toUpperCase())) {
      end = i;
      break;
    }
  }
  return end > start ? { from: start, to: end } : null;
}

/** Folded names a bare column reference may take, or `null` when the clause
 *  does not parse with confidence. */
function collectGroupedNames(
  tokens: readonly Token[],
  depths: readonly number[],
  clause: TokenSpan,
  rules: GroupByEngineRules,
): Set<string> | null {
  for (let i = clause.from; i < clause.to; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'kw' && UNPARSEABLE_GROUPING_WORDS.has(t.text.toUpperCase())) return null;
    // `GROUPING SETS` / `ROLLUP` lex as identifiers on some dialects.
    if (t.type === 'ident' && UNPARSEABLE_GROUPING_WORDS.has(t.text.toUpperCase())) return null;
  }

  const names = new Set<string>();
  let itemFrom = clause.from;
  for (let i = clause.from; i <= clause.to; i += 1) {
    const isBoundary =
      i === clause.to || (tokens[i]!.type === 'comma' && depths[i] === depths[clause.from]);
    if (!isBoundary) continue;
    const itemNames = parseGroupByItem(tokens, depths, itemFrom, i, rules);
    if (itemNames === null) return null;
    for (const n of itemNames) names.add(n);
    itemFrom = i + 1;
  }
  return names;
}

/** Folded names one GROUP BY item contributes, or `null` when its shape is
 *  not one the model recognises. */
function parseGroupByItem(
  tokens: readonly Token[],
  depths: readonly number[],
  from: number,
  to: number,
  rules: GroupByEngineRules,
): string[] | null {
  if (to <= from) return null;

  // A function call, a cast, a parenthesised expression, `GROUP BY ()` — the
  // legal reference is the whole expression and we cannot match it against a
  // flat column candidate.
  for (let i = from; i < to; i += 1) {
    const type = tokens[i]!.type;
    if (type === 'lparen' || type === 'rparen') return null;
    // A keyword inside an item (`COLLATE`, `CASE`, or a column whose name the
    // tokenizer reads as reserved) is an item shape we have not recognised.
    if (type === 'kw') return null;
  }

  // An ordinal — `GROUP BY 1` — stands for the n-th select-list item.
  if (to === from + 1 && tokens[from]!.type === 'literal') {
    if (!rules.ordinalInGroupBy) return null;
    const n = Number(tokens[from]!.text);
    if (!Number.isInteger(n) || n < 1) return null;
    const name = selectListItemColumn(tokens, depths, n);
    return name === null ? null : [name];
  }

  // Plain / qualified column refs and simple operator expressions over them:
  // harvest the tail segment of every dotted chain. `GROUP BY p.customer_id`
  // therefore admits both `customer_id` and a `p.customer_id`-labelled
  // candidate (candidates are matched on their last segment too).
  const names: string[] = [];
  for (let i = from; i < to; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'ident') {
      // A qualifier — the chain's tail is what names the column.
      if (i + 1 < to && tokens[i + 1]!.type === 'dot') continue;
      const { content } = parseIdentifierToken(t.text);
      if (content.length === 0) return null;
      const folded = content.toLowerCase();
      names.push(folded);
      // `GROUP BY total` where `total` is `SUM(amount) AS total` groups by the
      // aliased expression; where it is `payment.amount AS total` the
      // underlying column becomes groupable too.
      if (rules.aliasInGroupBy) {
        const aliased = selectListAliasColumn(tokens, depths, folded);
        if (aliased !== null) names.push(aliased);
      }
      continue;
    }
    if (t.type === 'dot' || t.type === 'op' || t.type === 'literal') continue;
    return null;
  }
  return names.length > 0 ? names : null;
}

/** The single depth-0 SELECT's item list, or `null` when the statement has
 *  none or more than one. */
function selectListSpan(
  tokens: readonly Token[],
  depths: readonly number[],
): TokenSpan | null {
  let selectIdx = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    if (depths[i] !== 0 || tokens[i]!.type !== 'kw') continue;
    if (tokens[i]!.text.toUpperCase() !== 'SELECT') continue;
    if (selectIdx !== -1) return null;
    selectIdx = i;
  }
  if (selectIdx === -1) return null;
  let end = tokens.length;
  for (let i = selectIdx + 1; i < tokens.length; i += 1) {
    if (depths[i] !== 0 || tokens[i]!.type !== 'kw') continue;
    if (SELECT_LIST_TERMINATORS.has(tokens[i]!.text.toUpperCase())) {
      end = i;
      break;
    }
  }
  return end > selectIdx + 1 ? { from: selectIdx + 1, to: end } : null;
}

/** Token spans of the depth-0 select-list items, in order. */
function selectListItems(
  tokens: readonly Token[],
  depths: readonly number[],
  span: TokenSpan,
): TokenSpan[] {
  const items: TokenSpan[] = [];
  let itemFrom = span.from;
  for (let i = span.from; i <= span.to; i += 1) {
    const isBoundary =
      i === span.to || (tokens[i]!.type === 'comma' && depths[i] === depths[span.from]);
    if (!isBoundary) continue;
    if (i > itemFrom) items.push({ from: itemFrom, to: i });
    itemFrom = i + 1;
  }
  return items;
}

/** The column the n-th (1-based) select-list item names, when that item is a
 *  plain column ref (optionally `AS`-aliased). `null` otherwise. */
function selectListItemColumn(
  tokens: readonly Token[],
  depths: readonly number[],
  n: number,
): string | null {
  const span = selectListSpan(tokens, depths);
  if (span === null) return null;
  const items = selectListItems(tokens, depths, span);
  const item = items[n - 1];
  return item ? plainColumnOfItem(tokens, item) : null;
}

/** The column an `AS <alias>`-bound select-list item names, when the aliased
 *  expression is a plain column ref. `null` otherwise. */
function selectListAliasColumn(
  tokens: readonly Token[],
  depths: readonly number[],
  foldedAlias: string,
): string | null {
  const span = selectListSpan(tokens, depths);
  if (span === null) return null;
  for (const item of selectListItems(tokens, depths, span)) {
    const bound = aliasOfItem(tokens, item);
    if (bound === null || bound !== foldedAlias) continue;
    return plainColumnOfItem(tokens, item);
  }
  return null;
}

/** The folded `AS`-bound alias of a select-list item, or `null`. */
function aliasOfItem(tokens: readonly Token[], item: TokenSpan): string | null {
  for (let i = item.to - 2; i >= item.from; i -= 1) {
    const t = tokens[i]!;
    if (t.type !== 'kw' || t.text.toUpperCase() !== 'AS') continue;
    const next = tokens[i + 1]!;
    if (next.type !== 'ident' && next.type !== 'kw') return null;
    const { content } = parseIdentifierToken(next.text);
    return content.length > 0 ? content.toLowerCase() : null;
  }
  return null;
}

/** The folded column name a select-list item is, ignoring a trailing
 *  `AS <alias>`. `null` when the item is not a plain (qualified) column ref. */
function plainColumnOfItem(tokens: readonly Token[], item: TokenSpan): string | null {
  let to = item.to;
  for (let i = item.from; i < item.to - 1; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'kw' && t.text.toUpperCase() === 'AS') {
      to = i;
      break;
    }
  }
  if (to <= item.from) return null;
  let last: string | null = null;
  for (let i = item.from; i < to; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'dot') continue;
    if (t.type !== 'ident') return null;
    const { content } = parseIdentifierToken(t.text);
    if (content.length === 0) return null;
    last = content.toLowerCase();
  }
  return last;
}

// ---------------------------------------------------------------------------
// Candidate name folding
// ---------------------------------------------------------------------------

/** The candidate's column name, folded: quotes stripped, last path segment,
 *  lower-cased. `filterText` wins because that is what the ranker matches. */
function foldCandidateName(candidate: CompletionCandidate): string {
  const raw = candidate.filterText ?? candidate.label;
  const tail = raw.slice(raw.lastIndexOf('.') + 1);
  return parseIdentifierToken(tail).content.toLowerCase();
}
